import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { useDomainStore } from '#modules/domains/stores/domain.store.js';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';

export const useDomain = () => {
    const route = useRoute();
    const router = useRouter();
    const domainStore = useDomainStore();
    const toast = useToast();
    const { t } = useI18n();

    const domainId = computed(() => route.params.domain);

    const { isPending, isLoading, isFetching, isError, error, refetch } = useQuery({
        queryKey: ['domain', domainId],
        queryFn: async () => await domainStore.fetchDomain(domainId),
        staleTime: 0, // Always refetch on navigation — store is the source of truth and only updates via fetchDomain
        retry: (failureCount, error) => {
            // Don't retry on 403/404 - param likely invalid
            if (error?.response && [403, 404].includes(error.response.status)) {
                return false;
            }
            return failureCount < 3;
        },
        enabled: computed(() => !!domainId.value),
    });

    const {
        domain,
        isActive,
        isCancelled,
        isExpired,
        isQuarantined,
        registrationFailed,
        transferFailed,
        canLock,
        isLocked,
        canEditContacts,
        status,
        registrantIdentity,
        billingIdentity,
        adminIdentity,
        techIdentity,
        tldRequirements,
        nameservers,
        subscription,
    } = storeToRefs(domainStore);

    // Handle errors and redirect
    watch(
        [isError, error],
        ([errorState, errorValue]) => {
            if (errorState && errorValue?.response) {
                const status = errorValue.response.status;
                if ([403, 404].includes(status)) {
                    void router.push({ name: 'domains' });
                    toast.error(t('domain.not_found'));
                }
            }
        },
        { immediate: true }
    );

    return {
        domainId,
        domain,
        isPending,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
        isActive,
        isCancelled,
        isExpired,
        isQuarantined,
        registrationFailed,
        transferFailed,
        canLock,
        isLocked,
        canEditContacts,
        status,
        registrantIdentity,
        billingIdentity,
        adminIdentity,
        techIdentity,
        tldRequirements,
        nameservers,
        subscription,
    };
};
